import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";

const log = createFileLogger('USER_INTERACTION');

export const userAskForDataCommand: SelfContainedCommand = {
  id: 'user_ask_for_data',
  description: `Collect structured data from the user via an inline form.

This command presents a form with specified fields and collects user input.
Use this when you need structured information that doesn't exist yet.

**Use Cases:**
• Collect vehicle information
• Get account credentials
• Request address details
• Gather structured user data

**Field Types:**
• string - Text input
• number - Numeric input
• boolean - Checkbox
• date - Date picker
• select - Dropdown with options`,
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'prompt',
        type: 'string',
        description: 'What to ask for (e.g., "Please provide your vehicle information")',
        required: true
      },
      {
        name: 'fields',
        type: 'array',
        description: 'Array of field definitions: [{name, type, description, required, options?}]',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          prompt: 'Please provide your vehicle information',
          fields: [
            { name: 'state', type: 'string', description: 'State (e.g., CA)', required: true },
            { name: 'licensePlate', type: 'string', description: 'License plate', required: true },
            { name: 'year', type: 'number', description: 'Year', required: false }
          ]
        },
        expectedOutput: createSuccessResult('Data collected', {
          data: { state: 'CA', licensePlate: '7ABC123', year: 2020 }
        }),
        description: 'Collect vehicle data',
        context: 'When creating a vehicle entity'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 10000,
    complexity: 'medium'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { prompt, fields } = params;
    
    try {
      log.info('[user_ask_for_data] Requesting data:', { prompt, fieldCount: fields?.length });
      
      if (!prompt || !fields || !Array.isArray(fields)) {
        return createErrorResult(
          'Missing required parameters',
          'prompt and fields (array) are required',
          'validation',
          { data: params }
        );
      }
      
      return createSuccessResult(
        'Data collected',
        {
          prompt,
          fields,
          data: {},
          inputType: 'form',
          timestamp: new Date().toISOString()
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            requiresUserInteraction: true
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[user_ask_for_data] Error:', error);
      return createErrorResult(
        'Data collection failed',
        `Failed to collect user data: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { prompt, fields },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};
